// ENG-3339: the department half of the old Notify modal, split out behind
// `inbox_action_clarity`. Notify keeps the email field; this modal owns the
// department chips and their capability validation. Both still call the same
// PUT /api/v1/receivers/update_tags with `tag` set to the chosen department
// name, so there is no API or backend difference between the two.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Form,
  Modal,
  Tag,
  message,
} from 'antd';

import ModalWrapper from './ModalWrapper';
import {
  hasFeatureInLocalStorage,
  INBOX_BACKGROUND_REFRESH_FEATURE,
} from '../../global/featureFlags';

const { CheckableTag } = Tag;

const errorStyle = {
  color: 'red',
};

// `modalType.data.record` is a single send token from the single-record entry
// points (row menu on one row, viewer toolbar) and an ARRAY of them from the two
// multi-select surfaces (ReferralActionBar, RowQuickActions on a selection).
const transferRecordCount = (record) => (Array.isArray(record) ? record.length : 1);

// One transfer moves N records out of the current user's view in one click, with
// no per-record undo — same class of consequence as the team reassign in the very
// same action bar, so it gets the same treatment: a Modal.confirm that states the
// count and the destination. Single-record transfers stay unconfirmed (the modal
// itself already names the one record's destination). Mirrors the shape of
// `confirmTeamReassign` in components/teams/teamPicker.jsx.
const confirmBulkTransfer = ({ count, department }) => {
  if (count <= 1) return Promise.resolve(true);
  return new Promise((resolve) => {
    Modal.confirm({
      title: `Transfer ${count} records?`,
      content: `All ${count} selected records will move to the ${department} department.`,
      okText: 'Transfer',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

class TransferToDepartment extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendTokens: props.modalType.data.record,
      departments: [],
      selectedDepartment: '',
      selectedRecordTypes: props.modalType.data.receiverType,
      departmentValidationError: '',
    };
  }

  async componentDidMount() {
    const { getOrganizationDepartments } = this.props;
    await getOrganizationDepartments();
    const { allOrganizationDepartments, modalType } = this.props;
    this.setState({ departments: allOrganizationDepartments });
    // Preselect the chip when the record is already tagged with a department.
    // eslint-disable-next-line array-callback-return
    allOrganizationDepartments.map((department) => {
      if (department.name === modalType.data.tag) {
        this.setState({ selectedDepartment: modalType.data.tag });
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Update selectedRecordTypes when modal data changes
    if (prevProps.modalType.data.receiverType !== this.props.modalType.data.receiverType) {
      this.setState({
        selectedRecordTypes: this.props.modalType.data.receiverType,
        departmentValidationError: '',
      });
    }
  }

  selectDepartment = (index) => {
    const { selectedRecordTypes, departments } = this.state;
    const selectedDept = departments[index];
    const userSelectedDepartment = selectedDept.name;
    let validationError;

    const recordTypesArray = Array.isArray(selectedRecordTypes)
      ? selectedRecordTypes
      : [selectedRecordTypes];

    // Validate department capabilities based on selected record types
    const missingCapabilities = [];
    if (recordTypesArray.includes('received_fax') && !selectedDept.has_active_fax) {
      missingCapabilities.push('active fax numbers');
    }
    if (recordTypesArray.includes('email_attachment') && !selectedDept.has_email) {
      missingCapabilities.push('email addresses');
    }

    if (missingCapabilities.length > 0) {
      validationError = `The selected department does not have ${missingCapabilities.join(' or ')} to reassign to.`;
    }

    this.setState({
      selectedDepartment: userSelectedDepartment,
      departmentValidationError: validationError,
    });
  };

  update = async (event) => {
    event.preventDefault();
    const { sendTokens, selectedDepartment } = this.state;
    const { editTag } = this.props;

    const confirmed = await confirmBulkTransfer({
      count: transferRecordCount(sendTokens),
      department: selectedDepartment,
    });
    if (!confirmed) return;

    await editTag(sendTokens, selectedDepartment);

    const {
      updatedTag,
      emailValidMessage,
      hideModal,
      noActiveFax,
      noActiveEmail,
      departmentName,
    } = this.props;

    if (noActiveFax || noActiveEmail) {
      const errors = [];
      if (noActiveFax) errors.push('active fax numbers');
      if (noActiveEmail) errors.push('email addresses');

      const errorMessage = `The ${departmentName} department does not have ${errors.join(' or ')} to reassign to.`;
      message.error({
        content: errorMessage,
      });
      return;
    }

    if (updatedTag) {
      hideModal();
      message.success({
        content: emailValidMessage,
      });
      this.updateRowSelected();
      await this.reconcileList();
    } else {
      message.error({
        content: emailValidMessage || 'Unable to transfer this record',
      });
    }
  };

  // `editReceivedRecordTag` asks for a background inbox refresh, but
  // `requestInboxTableRefresh` is a deliberate no-op while the organization's
  // `inbox_background_refresh` flag is off -- and that flag is independent of
  // `inbox_action_clarity`, so an organization can have this modal with no
  // background refresh behind it. A transfer moves the record's owning department
  // server-side, so with nothing reconciling the list the source department keeps
  // rendering a row its users can no longer open, alongside stale counts, until a
  // manual reload. That is worse than the usual stale row: the row is not merely
  // out of date, it is gone.
  //
  // Flag off, take the same legacy foreground refetch the comparable actions take
  // (`archiveRecordOpen` / `unarchiveRecordOpen` in useReferralActions,
  // `archiveRecord` in ReceivedRecords): guard on the flag, then refetch.
  // `refreshCurrentRecords` is the page-aware one, which is what this modal needs
  // -- it opens over /app/received, /app/assigned and /app/referred, and from the
  // viewers above any of them -- and unlike the archive path's
  // `window.location.reload()` it keeps the page and filters (they live in the URL
  // query) and does not wipe the success toast raised just above.
  //
  // Flag on, do nothing: the action's `requestInboxTableRefresh('notify')` already
  // covers it, and calling `refreshCurrentRecords` anyway would ride its own
  // background branch and fire a second, chart-labelled refetch.
  reconcileList = async () => {
    if (hasFeatureInLocalStorage(INBOX_BACKGROUND_REFRESH_FEATURE)) return;
    const { refreshCurrentRecords } = this.props;
    await refreshCurrentRecords();
  };

  updateRowSelected() {
    const { selectedDepartment } = this.state;
    const { modalType, rowSelected } = this.props;
    const selectedRow = {
      tag: selectedDepartment,
      index: modalType.data.record.index,
    };
    rowSelected([selectedRow]);
  }

  renderModal = () => {
    const { isLoading, modalType } = this.props;
    const { departments, selectedDepartment, departmentValidationError } = this.state;
    if (isLoading) {
      return (<span> Transferring record, Please wait... </span>);
    }

    const { receiverType } = modalType.data;
    const isEmailAttachment = Array.isArray(receiverType)
      ? receiverType.every((type) => type === 'email_attachment')
      : receiverType === 'email_attachment';

    // The entry points already say "Transfer to department 3 records"; the modal
    // must not then talk about "this record".
    const count = transferRecordCount(modalType.data.record);
    const prompt = count > 1
      ? `Select the department these ${count} records should move to.`
      : 'Select the department this record should move to.';

    const emailDepartmentNote = `Note: ${count > 1 ? 'Each email attachment' : 'The email attachment'} will be assigned to the department's first email address, if available.
      If the department has no email addresses, you'll be notified and the assignment will not take place.`;

    const faxDepartmentNote = `Note: ${count > 1 ? 'Each received fax' : 'The received fax'} will be assigned to the department's first active fax number, if available.
      If the department has no active fax numbers, you'll be notified and the assignment will not take place.`;

    return (
      <Form
        onFinish={this.update}
        id="transferToDepartment"
      >
        <p style={{ marginBottom: '5px' }}>{prompt}</p>
        <div style={{ fontSize: '12px', color: 'grey', marginBottom: '10px' }}>
          {isEmailAttachment ? (
            <p>{emailDepartmentNote}</p>
          ) : (
            <p>{faxDepartmentNote}</p>
          )}
        </div>
        {departmentValidationError && (
          <p style={errorStyle}>{departmentValidationError}</p>
        )}
        <Form.Item
          name="department"
        >
          {departments && departments.map((department, index) => (
            <CheckableTag
              key={department.name}
              checked={selectedDepartment === department.name}
              style={{ cursor: 'pointer', border: '1px dotted #1890ff' }}
              onClick={() => this.selectDepartment(index)}
            >
              {department.name}
            </CheckableTag>
          ))}
        </Form.Item>
      </Form>
    );
  };

  render() {
    const { isLoading, hideModal } = this.props;
    const { selectedDepartment, departmentValidationError } = this.state;
    const customContentStyle = {
      width: '340px',
    };

    const isSubmitDisabled = isLoading
      || !selectedDepartment
      || departmentValidationError;

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.update}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={isSubmitDisabled}
        modalTitle="Transfer to department"
        form="transferToDepartment"
        isDraggable={true}
      >
        {this.renderModal()}
      </ModalWrapper>
    );
  }
}

TransferToDepartment.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  editTag: PropTypes.func.isRequired,
  rowSelected: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  emailValidMessage: PropTypes.string.isRequired,
  updatedTag: PropTypes.bool.isRequired,
  allOrganizationDepartments: PropTypes.array.isRequired,
  getOrganizationDepartments: PropTypes.func.isRequired,
  refreshCurrentRecords: PropTypes.func.isRequired,
  noActiveFax: PropTypes.bool,
  departmentName: PropTypes.string,
  noActiveEmail: PropTypes.bool,
};

TransferToDepartment.defaultProps = {
  noActiveFax: false,
  departmentName: '',
  noActiveEmail: false,
};

export default connect((state) => ({
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  updatedTag: state.records.updatedTag,
  departments: state.userProfile.departments,
  allOrganizationDepartments: state.userProfile.allOrganizationDepartments,
  noActiveFax: state.records.noActiveFax,
  departmentName: state.records.departmentName,
  noActiveEmail: state.records.noActiveEmail,
}))(TransferToDepartment);
